<template>
  <div class="variable-mapping-popover">
    <div class="backdrop"></div>
    <div class="content" ref="content">
      <div class="header">
        <div class="left">
          <h2>{{ $t("variable-mapping.popover-title") }}</h2>
          <infobox
            right
            bottom
            :width="400"
            class="info-box generate-warning"
            slot="info"
          >
            <span slot="icon">
              <i class="fa-light fa-question-circle"></i>
            </span>
            <p
              class="text-small description"
              v-html="$t('variable-mapping.popover-description')"
            ></p>
          </infobox>
        </div>
        <div class="close-popover" @click="$emit('close')">
          <i class="fa-light fa-times"></i>
        </div>
      </div>

      <div class="selectors">
        <div class="id-selector">
          <custom-input
            ref="idInput"
            v-model="selectedAsId"
            list="filedata-variable-list"
            :inputType="'text'"
            :name="'select-id-dropdown'"
            :label="$t('variable-mapping.id-select-label')"
            :placeholder="$t('variable-mapping.id-select-placeholder')"
            :invalidInput="invalidIdInput"
            @focus="idFocused = true"
            @blur="idFocused = false"
          />
          <div
            @click="resetIdFilter"
            v-show="selectedAsId"
            class="reset-filter"
          >
            <i class="fa-light fa-times"></i>
          </div>
        </div>
        <div class="reference-selector">
          <custom-input
            ref="nameInput"
            v-model="selectedAsName"
            list="filedata-variable-list"
            :inputType="'text'"
            :name="'select-reference-dropdown'"
            :label="$t('variable-mapping.name-select-label')"
            :placeholder="$t('variable-mapping.name-select-placeholder')"
            :invalidInput="invalidNameInput"
            @focus="nameFocused = true"
            @blur="nameFocused = false"
          />
          <div
            @click="resetNameFilter"
            v-show="selectedAsName"
            class="reset-filter"
          >
            <i class="fa-light fa-times"></i>
          </div>
        </div>
        <datalist id="filedata-variable-list">
          <option v-for="(variable, index) in fileData.variables" :key="index">
            {{ variable.id }}
          </option>
        </datalist>
      </div>
      <div class="variable-list-header">
        <h4 class="list-header-id">Id</h4>
        <h4 class="list-header-name">{{ $t("variable-mapping.name") }}</h4>
        <h4 class="list-header-examnple">
          {{ $t("variable-mapping.example") }}
        </h4>
        <div class="list-header-type">
          <h4>Type</h4>
          <infobox
            left
            top
            :width="400"
            class="info-box generate-warning"
            slot="info"
          >
            <span slot="icon">
              <a
                href="https://docs.icrobotics.com/pages/customdatasources/dataformat/#variable-types"
                target="_blank"
              >
                <i class="fa-light fa-question-circle"></i>
              </a>
            </span>
            <p>{{ $t("variable-mapping.type-helper-text") }}</p>
          </infobox>
        </div>
      </div>
      <div
        class="variable-item-list custom-scrollbar-light"
        :style="contentHeight"
      >
        <mapping-item
          :variable="variable"
          v-for="variable in fileData.variables"
          :key="variable.id"
        />
      </div>
      <div class="actions">
        <button
          type="button"
          class="btn btn-md btn-primary"
          @click="$emit('save-changes')"
          :disabled="!valid"
          :tooltip="!valid ? $t('variable-mapping.invalid-tooltip') : ''"
        >
          {{ $t("done") }}
        </button>
        <button
          type="reset"
          class="btn btn-md btn-secondary"
          @click="$emit('close')"
        >
          {{ $t("cancel") }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import CustomInput from "@/components/CustomInput";
import MappingItem from "@/components/MappingItem";
import Infobox from "@/components/Infobox";
export default {
  data() {
    return {
      idFocused: false,
      nameFocused: false,
      idSearchFilter: "",
      idDropdownVisible: false,
      selectedAsId: null,
      selectedAsName: null,
      nameSearchFilter: "",
      nameDropdownVisible: false,
      contentSize: 0 + "px",
    };
  },
  props: {
    fileData: Object,
  },
  components: {
    MappingItem,
    Infobox,
    CustomInput,
  },
  methods: {
    setContentSize() {
      console.log("set size");
      this.contentSize = this.$refs.content.clientHeight + "px";
    },
    resetIdFilter() {
      this.selectedAsId = null;
      this.$refs.idInput.focus();
    },
    resetNameFilter() {
      this.selectedAsName = null;
      this.$refs.nameInput.focus();
    },
  },
  computed: {
    filteredIds() {
      return this.fileData.variables.filter((v) => {
        return (
          (v.name || "")
            .toLowerCase()
            .indexOf(this.idSearchFilter.toLowerCase()) !== -1
        );
      });
    },
    valid() {
      return (
        this.selectedAsId &&
        this.selectedAsName &&
        this.fileData.variables.some((v) => v.isId == true) &&
        this.fileData.variables.some((v) => v.isReferenceName == true)
      );
    },
    contentHeight() {
      return { "--content-height": this.contentSize };
    },
    invalidNameInput() {
      return (
        !this.nameFocused &&
        this.selectedAsName &&
        !this.fileData.variables
          .map((variable) => variable.id)
          .includes(this.selectedAsName)
      );
    },
    invalidIdInput() {
      return (
        !this.idFocused &&
        this.selectedAsId &&
        !this.fileData.variables
          .map((variable) => variable.id)
          .includes(this.selectedAsId)
      );
    },
  },
  mounted() {
    this.setContentSize();
    window.addEventListener("resize", this.setContentSize);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.setContentSize);
  },
  watch: {
    selectedAsId: {
      handler: function () {
        this.fileData.variables.forEach((variable) => {
          if (variable.id == this.selectedAsId) {
            variable.isId = true;
          } else {
            variable.isId = false;
          }
        });
      },
    },
    selectedAsName: {
      handler: function () {
        this.fileData.variables.forEach((variable) => {
          if (variable.id == this.selectedAsName) {
            variable.isReferenceName = true;
          } else {
            variable.isReferenceName = false;
          }
        });
      },
    },
  },
};
</script>

<style lang="less">
.variable-mapping-popover {
  .backdrop {
    position: fixed;
    z-index: 99;
    height: 100%;
    width: 100%;
    left: 0;
    top: 0;
    background-color: rgba(0, 0, 0, 0.25);
  }
  .header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background-color: var(--background-white);
    box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.2);
    position: relative;
    padding: 14px 24px 14px 24px;
    border-radius: 10px 10px 0 0;

    h2 {
      margin-bottom: 0;
    }
    .description {
      margin: 6px 0 0 0;
      padding: 0;
    }
    .close-popover {
      cursor: pointer;
    }

    .left {
      display: flex;
      align-items: center;
      gap: 4px;
    }
  }
  .content {
    position: fixed;
    box-shadow: 0px 0px 5px rgba(0, 0, 0, 0.25);
    width: 90%;
    height: 90%;
    left: calc(50% + (85px / 2));
    top: 50%;
    transform: translate(-50%, -50%);
    z-index: 99;
    background-color: var(--background-white);
    border-radius: var(--standard-border-radius);
  }
  // .content{
  //   position: relative;
  // }
  .mapping-list {
    padding: 0 24px 24px 24px;
  }
  .variable-item-list {
    height: calc(var(--content-height) - 270px);
    overflow-y: auto;
    padding: 6px 24px;
  }
  .variable-list-header {
    margin: 0 24px;
    display: grid;
    grid-template-columns: 1fr 1fr 1fr 150px;
    padding: 12px 6px;
    gap: 12px;

    h4 {
      margin: 0;
    }
    .list-header-id {
      padding-left: 6px;
    }
    .list-header-type {
      display: flex;
      gap: 4px;
      svg {
        margin-bottom: 1px;
      }
    }
  }
  .actions {
    position: absolute;
    bottom: 24px;
    right: 24px;
  }
  .selector-section {
    display: grid;
    grid-template-columns: 1fr 2fr;
    align-items: center;
    justify-content: space-between;
  }
  .selectors {
    padding: 6px 24px;
    display: flex;
    flex-direction: row;
    margin: 0 0 24px 0;
    gap: 24px;
    .id-selector,
    .reference-selector {
      position: relative;
      width: 30%;

      .reset-filter {
        cursor: pointer;
        position: absolute;
        right: 24px;
        bottom: 7px;
      }
    }
  }
}
</style>